import {
	createStore,
	type StoreActionApi
} from 'react-sweet-state';

import type { PromptInputContext } from '@atlassian/conversation-assistant-external-context/types';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { type SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

import { isTextFile } from '../../utils/fileTypeHelpers';
import { generateFilePreview, looksLikeImage } from '../../utils/generatePreview';

import { cleanupBlobUrls } from './cleanupBlobUrls';
import { defaultStateContextPerKey } from './defaultStateContextPerKey';
import type { ExternalContextTypeAttachment } from './ExternalContextTypeAttachment';
import type { FileTypeAttachment } from './FileTypeAttachment';
import type { State } from './State';
import { updateAttachements } from './updateAttachements';
import type { UploadedFile } from './UploadedFile';

const actions = {
		addFiles:
			(
				key: string,
				files: UploadedFile[],
				sendAnalyticsEvent: SendAnalyticsEvent,
			): (({ getState, dispatch }: StoreActionApi<State>) => void) =>
			({ getState: _getState, dispatch }) => {
				const needsPreview = (file: UploadedFile) =>
					file.fileObject &&
					(looksLikeImage(file.fileObject) ||
						isTextFile(file.fileObject.type, file.fileObject.name));

				// Add files with preview loading state if needed
				const fileAttachments: [string, FileTypeAttachment][] = files.map((file) => [
					file.name,
					{
						type: 'file',
						data: needsPreview(file) ? { ...file, isPreviewLoading: true } : file,
					},
				]);
				dispatch(updateAttachements(key, fileAttachments));

				files.forEach((file) => {
					if (needsPreview(file) && file.fileObject) {
						generateFilePreview(file.fileObject)
							.then((previewBlobUrl) => {
								// Check if file still exists and wasn't removed/errored during preview generation
								const currentState = _getState()[key] || defaultStateContextPerKey;
								const currentAttachment = currentState.attachments.get(file.name);

								if (
									!currentAttachment ||
									currentAttachment.type !== 'file' ||
									currentAttachment.data.error
								) {
									// File was removed or errored while preview was generating, cleanup the blob
									URL.revokeObjectURL(previewBlobUrl);
									return;
								}

								dispatch(
									store.actions.updateFileWithName(key, file.name, {
										previewBlobUrl,
										isPreviewLoading: false,
									}),
								);
							})
							.catch((error) => {
								// Log error to Sentry for monitoring
								logException({
									exception: error,
									name: 'file-preview-generation-error',
									tags: {
										fileType: file.fileObject?.type,
									},
								});

								sendAnalyticsEvent({
									action: 'failed',
									actionSubject: 'preview',
									actionSubjectId: 'thumbnailGeneration',
									attributes: {
										fileType: file.fileObject?.type || 'unknown',
									},
								});

								dispatch(
									store.actions.updateFileWithName(key, file.name, {
										isPreviewLoading: false,
										previewError: error instanceof Error ? error.message : String(error),
									}),
								);
							});
					}
				});
			},
		addExternalContext:
			(
				key: string,
				context: PromptInputContext[],
			): (({ getState, dispatch }: StoreActionApi<State>) => void) =>
			({ getState: _getState, dispatch }) => {
				const externalContextAttachments: [string, ExternalContextTypeAttachment][] = context.map(
					(context) => [
						context.key,
						{ type: 'external', data: context } as ExternalContextTypeAttachment,
					],
				);
				dispatch(updateAttachements(key, externalContextAttachments));
			},
		updateFileWithName:
			(
				key: string,
				fileName: string,
				fileData: Partial<UploadedFile>,
			): (({ getState, dispatch }: StoreActionApi<State>) => void) =>
			({ getState, dispatch }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;
				const attachment = currentAttachtments.get(fileName);
				if (attachment && attachment.type === 'file') {
					dispatch(
						updateAttachements(key, [
							[
								fileName,
								{ ...attachment, data: { ...attachment.data, ...fileData } } as FileTypeAttachment,
							],
						]),
					);
				}
			},
		removeFile:
			(key: string, fileName: string): (({ getState, dispatch }: StoreActionApi<State>) => void) =>
			({ getState, dispatch }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				// Cleanup blob URL before removing
				const attachment = currentAttachtments.get(fileName);
				if (attachment) {
					cleanupBlobUrls([attachment]);
				}

				dispatch(
					updateAttachements(
						key,
						[...currentAttachtments.entries()].filter(
							([entryKey, value]) => value.type !== 'file' || entryKey !== fileName,
						),
						true,
					),
				);
			},
		removeExternalContext:
			(key: string, id: string): (({ getState, dispatch }: StoreActionApi<State>) => void) =>
			({ getState, dispatch }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				dispatch(
					updateAttachements(
						key,
						[...currentAttachtments.entries()].filter(
							([entryKey, value]) => value.type !== 'external' || entryKey !== id,
						),
						true,
					),
				);
			},
		clearFiles:
			(key: string): (({ dispatch, getState }: StoreActionApi<State>) => void) =>
			({ dispatch, getState }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				// Cleanup all file blob URLs
				cleanupBlobUrls([...currentAttachtments.values()]);

				dispatch(
					updateAttachements(
						key,
						[...currentAttachtments.entries()].filter(([_, value]) => value.type !== 'file'),
						true,
					),
				);
			},
		clearAttachments:
			(key: string): (({ dispatch, getState }: StoreActionApi<State>) => void) =>
			({ dispatch, getState }) => {
				const { attachments: currentAttachtments } = getState()[key] || defaultStateContextPerKey;

				// Cleanup all blob URLs
				cleanupBlobUrls([...currentAttachtments.values()]);

				dispatch(updateAttachements(key, [], true));
			},
};

export type Actions = typeof actions;

export const store: ReturnType<typeof createStore<State, Actions>> = createStore<State, Actions>({
	name: 'conversationAttachments',
	initialState: {} as State,
	actions,
});
